<script setup lang="ts">
import { computed } from 'vue';
import { trans } from '@elements/translation';
import Icon from '@components/Icon.vue';
import IconCaret from '@icons/ph_caret-up-down-bold.svg';
import Typography from '@components/Typography.vue';
import { SelectionMobileView } from '@enums/selectionMobileView.ts';

const { view } = defineProps<{
  view: SelectionMobileView;
}>();

const emit = defineEmits<{(e: 'change', payload: SelectionMobileView): void;}>();
const mapDisplayed = computed<boolean>(() => view === SelectionMobileView.MAP);

const options = new Map([
  [ SelectionMobileView.LIST, trans('mobile.view.option.list') ],
  [ SelectionMobileView.MAP, trans('mobile.view.option.map') ],
  [ SelectionMobileView.COMPARATOR, trans('mobile.view.option.comparator') ],
  [ SelectionMobileView.DASHBOARD, trans('mobile.view.option.dashboard_table') ],
]);

const currentLabel = computed<string>(() => options.get(view) ?? '');

const onChange = (e: Event): void => {
  const target = e.target as HTMLSelectElement;
  const value = target.value as SelectionMobileView;
  emit('change', value);
};
</script>

<template>
  <label
    :class="[
      'fixed inset-x-2',
      'flex items-center justify-center gap-2',
      'box-border h-12 w-fit',
      'mx-auto px-3 py-4',
      'cursor-pointer rounded-lg border border-solid border-gray bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.10)]',
      { 'bottom-[72px]': !mapDisplayed },
    ]"
  >
    <Icon :svg="IconCaret" width="20" height="20" fill="gray-darker" />

    <Typography
      class="pointer-events-none"
      variant="body-bold"
      color="black"
    >
      {{ currentLabel }}
    </Typography>

    <select
      :class="[
        'absolute',
        'inset-0 appearance-none opacity-0',
        'cursor-pointer'
      ]"
      :value="view"
      @change="onChange"
    >
      <option
        v-for="[ value, label ] in options.entries()"
        :key="value"
        :value="value"
      >
        {{ label }}
      </option>
    </select>
  </label>
</template>
